import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ToolTip from "components/design/toolTip";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Loader from "components/DesignSystem/Loader/loader";
import { Search } from "components/DesignSystem/Search/Search";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { CopyIcon } from "components/icons/CopyIcon";
import { SavedToInkleDocsModal } from "components/SavedToInkleDocsModal/SavedToInkleDocsModal";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";
import { SUMMARY_REPORT } from "constants/bookkeeping";
import { motion } from "framer-motion";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useElementDimensions } from "hooks/useElementDimensions";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ReportsType } from "pages/Books/Reports/Reports";
import { Fragment, useMemo, useState } from "react";
import {
  useGetVendorSummaryQuery,
  useLazyExportVendorSummaryQuery,
  VendorSummaryItem,
  VendorSummaryVendorList,
} from "store/apis/reports";
import ThreeDots from "static/images/ThreeDots.svg";
import { BackendError } from "types/utils/error";
import { ReportFilterType } from "./ReportFilter";
import { TransactionsTable } from "./ReportsAccordion";
import {
  ExpandedRow,
  getTransactionReportHeading,
} from "./ExpandedTransaction";
import { Column } from "react-table-8.10.7";
import { AccountWithMonths } from "./utils/formatViewReportData";
import { PeriodType } from "constants/revenueMetrics";
import { INCOME_STATEMENT } from "constants/bookkeeping";
import { TOTALS_COLUMN } from "constants/reports";

type VendorTxnCell = { rowIdx: number; periodKey: string } | null;

const buildTableRows = (
  vendors: VendorSummaryVendorList[],
  periodKeys: string[],
  summablePeriodKeys: string[]
): string[][] => {
  const header = ["Vendor", ...periodKeys];
  const rows = vendors.map((vendor) => [
    vendor.name,
    ...periodKeys.map((key) => {
      if (key === TOTALS_COLUMN) {
        const rowTotal = summablePeriodKeys.reduce(
          (sum, summableKey) => sum + (vendor.period[summableKey]?.amount ?? 0),
          0
        );
        return String(rowTotal);
      }

      const amount = vendor.period[key]?.amount;
      return amount != null ? String(amount) : "";
    }),
  ]);
  return [header, ...rows];
};

const rowsToTsv = (rows: string[][]): string =>
  rows.map((row) => row.join("\t")).join("\n");

export const VendorBreakdownRow = ({
  categoryId,
  categoryDepth = 0,
  filter,
  reportType,
  displayPeriodKeys,
  parentColumns,
  columnWidths,
  scrollContainerWidth,
  parentColumnCount,
  selectedRows,
  setSelectedRows,
}: {
  categoryId: string;
  categoryDepth?: number;
  filter: ReportFilterType;
  reportType: ReportsType;
  displayPeriodKeys: string[];
  parentColumns: Column<AccountWithMonths>[];
  columnWidths: Record<string, number>;
  scrollContainerWidth: number;
  parentColumnCount: number;
  onClose: () => void;
  selectedRows?: {};
  setSelectedRows?: React.Dispatch<React.SetStateAction<{}>>;
}) => {
  const entityId = useCurrentEntityId();
  const [vendorTxnCell, setVendorTxnCell] = useState<VendorTxnCell>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [savedDocumentId, setSavedDocumentId] = useState<string>();
  const [ref, { height }] = useElementDimensions();
  const { copyToClipboard } = useCopyToClipboard();
  const { alertToast, successToast } = useToast();
  const selectFolderModal = useModal();
  const savedToInkleDocsModal = useModal();

  const { data: vendorData = [], isFetching } = useGetVendorSummaryQuery(
    {
      entityId,
      startDate: filter.START_DATE,
      endDate: filter.END_DATE,
      reportType: (filter.VIEW_BY || "MONTHLY") as any,
      accountingMethod: filter.ACCOUNTING_METHOD,
      vendorNames: [],
      comparisonSummaryType: SUMMARY_REPORT.CATEGORY,
      categoryList: [categoryId],
    },
    { skip: !entityId || !categoryId }
  );

  const [exportVendorSummary, { isFetching: isExporting }] =
    useLazyExportVendorSummaryQuery();

  const categoryItem = vendorData.find(
    (item): item is Extract<VendorSummaryItem, { vendor_list: any }> =>
      "vendor_list" in item
  );
  const vendors = categoryItem?.vendor_list ?? [];

  const nameColId = parentColumns[0]?.id;
  const actionsColId = parentColumns[parentColumns.length - 1]?.id;
  const nameColSize =
    (nameColId && columnWidths[nameColId]) ||
    parentColumns[0]?.getSize() ||
    300;
  const actionsColSize =
    (actionsColId && columnWidths[actionsColId]) ||
    parentColumns[parentColumns.length - 1]?.getSize() ||
    90;
  const leftPinWidth = nameColSize;
  const rightPinWidth = actionsColSize;
  const hasTotalsColumn =
    reportType === INCOME_STATEMENT &&
    displayPeriodKeys.includes(TOTALS_COLUMN);
  const showPinnedTotalColumn = !hasTotalsColumn;
  const summablePeriodKeys = hasTotalsColumn
    ? displayPeriodKeys.filter((key) => key !== TOTALS_COLUMN)
    : displayPeriodKeys;

  const filteredVendors = useMemo(() => {
    if (!searchQuery.trim()) return vendors;
    const query = searchQuery.toLowerCase();
    return vendors.filter((v) => v.name.toLowerCase().includes(query));
  }, [vendors, searchQuery]);

  const filteredVendorCount = useMemo(() => {
    if (!searchQuery.trim()) return vendors.length;
    const query = searchQuery.toLowerCase();
    return vendors.filter((v) => v.name.toLowerCase().includes(query)).length;
  }, [vendors, searchQuery]);

  const getVendorRowTotal = (vendor: VendorSummaryVendorList) =>
    summablePeriodKeys.reduce(
      (sum, key) => sum + (vendor.period[key]?.amount ?? 0),
      0
    );

  const totals = useMemo(() => {
    const periodTotals = displayPeriodKeys.reduce<Record<string, number>>(
      (acc, key) => {
        acc[key] = vendors.reduce(
          (sum, vendor) => sum + (vendor.period[key]?.amount ?? 0),
          0
        );
        return acc;
      },
      {}
    );

    if (hasTotalsColumn) {
      periodTotals[TOTALS_COLUMN] = vendors.reduce(
        (sum, vendor) =>
          sum +
          summablePeriodKeys.reduce(
            (rowSum, key) => rowSum + (vendor.period[key]?.amount ?? 0),
            0
          ),
        0
      );
    }

    return periodTotals;
  }, [vendors, displayPeriodKeys, hasTotalsColumn, summablePeriodKeys]);

  const handleAmountClick = (
    e: React.MouseEvent,
    rowIdx: number,
    periodKey: string,
    amount: number | null
  ) => {
    e.stopPropagation();
    if (!amount) return;
    setVendorTxnCell((prev) =>
      prev?.rowIdx === rowIdx && prev?.periodKey === periodKey
        ? null
        : { rowIdx, periodKey }
    );
  };

  const handleCopy = () => {
    const rows = buildTableRows(vendors, displayPeriodKeys, summablePeriodKeys);
    copyToClipboard(rowsToTsv(rows));
  };

  const exportSummary = async (
    exportToMail: boolean = false,
    folderId?: string
  ) => {
    try {
      const result = await exportVendorSummary({
        entityId,
        startDate: filter.START_DATE,
        endDate: filter.END_DATE,
        reportType: (filter.VIEW_BY || "MONTHLY") as PeriodType,
        accountingMethod: filter.ACCOUNTING_METHOD as "ACCRUAL" | "CASH",
        vendorNames: [],
        comparisonSummaryType: SUMMARY_REPORT.CATEGORY,
        categoryList: [categoryId],
        export_to_mail: exportToMail,
        folderId,
      }).unwrap();

      if (folderId) {
        setSavedDocumentId(result.document_id);
        savedToInkleDocsModal.open();
        return;
      }

      if (!exportToMail && result.download_url) {
        window.open(result.download_url, "_blank");
      }

      successToast({
        message: exportToMail ? "Report sent to email" : "Report exported",
      });
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const content = isFetching ? (
    <div className="t-flex t-items-center t-py-3 t-px-4 t-sticky sm:!t-static t-left-0 t-bg-white t-h-14 t-w-full">
      <Loader size="small" />
    </div>
  ) : (
    <>
      {/* Header */}
      <div className="t-flex t-items-center t-justify-between t-border-0 t-border-b t-border-solid t-border-neutral-0 t-sticky sm:!t-static t-top-0 t-z-[2] !t-bg-surface-lighter-grey t-h-[52px]">
        <div
          className="t-flex t-items-center t-gap-2 t-pr-3 t-h-full t-flex-shrink-0 t-sticky sm:!t-static t-left-0 !t-bg-surface-lighter-grey t-z-[2]"
          style={{
            minWidth: nameColSize,
            paddingLeft: `calc(0.75rem + ${categoryDepth + 1}rem + 1.25rem)`,
          }}
        >
          <span className="t-text-subtext-sm t-font-semibold t-text-text-60">
            Vendors
          </span>
          {vendors.length > 0 && (
            <Tag tagType="gray" icon={false}>
              {filteredVendorCount} vendors
            </Tag>
          )}
        </div>
        <div
          className="t-flex t-items-center t-gap-2 t-flex-shrink-0 t-sticky sm:!t-static !t-bg-surface-lighter-grey t-px-2"
          style={{ right: `${rightPinWidth}px` }}
        >
          <Search
            placeholder="Search vendors"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            customSize="small"
            className="!t-bg-white"
          />
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <span>
                <Button customType="ghost_icon" size="xsmall">
                  <img
                    src={ThreeDots}
                    alt="More options"
                    className="t-select-none"
                  />
                </Button>
              </span>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content align="end" sideOffset={4}>
                <Dropdown.Item onSelect={handleCopy} icon={<CopyIcon />}>
                  Copy to clipboard
                </Dropdown.Item>
                <Dropdown.Sub>
                  <Dropdown.SubTrigger>Export</Dropdown.SubTrigger>
                  <Dropdown.Portal>
                    <Dropdown.SubContent>
                      <ExportOptions
                        onLocalDownload={() => exportSummary(false)}
                        onMailExport={() => exportSummary(true)}
                        onSaveInInkleDocs={selectFolderModal.open}
                      />
                    </Dropdown.SubContent>
                  </Dropdown.Portal>
                </Dropdown.Sub>
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        </div>
      </div>

      {vendors.length === 0 && (
        <div
          className="t-px-3 t-py-3 t-text-body t-text-text-30 t-sticky sm:!t-static t-left-0 t-bg-white"
          style={{ width: nameColSize }}
        >
          No vendor data available.
        </div>
      )}

      {vendors.length > 0 && filteredVendors.length === 0 && (
        <div
          className="t-px-3 t-py-3 t-text-body t-text-text-30 t-sticky sm:!t-static t-left-0 t-bg-white"
          style={{ width: nameColSize }}
        >
          No vendors match your search.
        </div>
      )}

      {filteredVendors.map((baseVendor, rowIdx) => {
        const isTxnOpen = vendorTxnCell?.rowIdx === rowIdx;
        const activePeriodKey = isTxnOpen ? vendorTxnCell?.periodKey : null;
        const periodValue = activePeriodKey
          ? baseVendor.period[activePeriodKey]
          : null;

        return (
          <Fragment key={rowIdx}>
            <div className="t-flex t-items-center t-border-0 t-border-b t-border-solid t-border-neutral-0 hover:t-bg-surface-lighter-grey t-group t-transition-colors">
              <div
                className="t-sticky sm:!t-static t-left-0 t-bg-white t-z-[2] t-pr-3 t-py-2.5 t-text-text-60 t-flex-shrink-0 t-text-body-sm t-drop-shadow-table-left-pin t-flex t-items-center t-gap-2 t-overflow-hidden group-hover:t-bg-surface-lighter-grey t-transition-colors"
                style={{
                  width: nameColSize,
                  paddingLeft: `calc(0.75rem + ${
                    categoryDepth + 1
                  }rem + 1.25rem)`,
                }}
              >
                <Avatar
                  src={baseVendor.logo}
                  alt={baseVendor.name}
                  size="extra-small"
                />
                <ToolTip text={baseVendor.name}>
                  <span className="t-truncate t-min-w-0">
                    {baseVendor.name}
                  </span>
                </ToolTip>
              </div>
              {displayPeriodKeys.map((key) => {
                const isTotalsColumn = key === TOTALS_COLUMN;
                const col = parentColumns.find((c) => c.id === key);
                const amount = isTotalsColumn
                  ? getVendorRowTotal(baseVendor)
                  : baseVendor.period[key]?.amount ?? null;
                const isExpanded =
                  isTxnOpen && vendorTxnCell?.periodKey === key;
                const isClickable =
                  !isTotalsColumn && amount !== null && amount !== 0;
                const colWidth = columnWidths[key] || col?.getSize() || 114;

                return (
                  <div
                    key={key}
                    className={classNames(
                      "t-px-3 t-py-2.5 t-flex-shrink-0 t-text-text-60 t-text-body-sm",
                      {
                        "!t-bg-surface-lighter-grey": isExpanded && isClickable,
                        "hover:t-cursor-pointer hover:t-text-purple hover:t-underline":
                          isClickable,
                      }
                    )}
                    style={{ minWidth: colWidth }}
                    onClick={(e) =>
                      isClickable && handleAmountClick(e, rowIdx, key, amount)
                    }
                  >
                    <div
                      className={classNames(
                        "t-flex t-items-center t-justify-end t-ml-4",
                        {
                          "!t-text-purple !t-underline":
                            isExpanded && isClickable,
                          "t-font-medium": isTotalsColumn,
                        }
                      )}
                    >
                      {amount !== null ? (
                        <AmountSuperScript amount={amount} />
                      ) : (
                        <span className="t-text-text-30">-</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {isTxnOpen && vendorTxnCell && periodValue && (
              <ExpandedRow
                colSpan={parentColumnCount}
                leftPinWidth={leftPinWidth}
                rightPinWidth={rightPinWidth}
                scrollContainerWidth={scrollContainerWidth}
              >
                <TransactionsTable
                  reportType={reportType}
                  categoryId={categoryId}
                  merchantId={baseVendor.vendor_id}
                  startDate={periodValue.start_date}
                  endDate={periodValue.end_date}
                  accountingMethod={filter.ACCOUNTING_METHOD}
                  isComparisonReport
                  transactionsReportHeading={getTransactionReportHeading(
                    periodValue.end_date,
                    baseVendor.name,
                    filter.VIEW_BY
                  )}
                  handleClose={() => setVendorTxnCell(null)}
                  class_ids={filter.CLASS_IDS}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                />
              </ExpandedRow>
            )}
          </Fragment>
        );
      })}

      {vendors.length > 0 && (
        <div className="t-flex t-items-center t-border-0 t-border-t t-border-solid t-border-neutral-0 !t-bg-surface-lighter-grey">
          <div
            className="t-sticky sm:!t-static t-left-0 !t-bg-surface-lighter-grey t-z-[2] t-pr-3 t-py-2.5 t-text-text-60 t-flex-shrink-0 t-text-body-sm t-font-semibold t-drop-shadow-table-left-pin"
            style={{
              minWidth: nameColSize,
              paddingLeft: `calc(0.75rem + ${categoryDepth + 1}rem + 1.25rem)`,
            }}
          >
            Total
          </div>
          {displayPeriodKeys.map((key) => {
            const col = parentColumns.find((c) => c.id === key);
            const colWidth = columnWidths[key] || col?.getSize() || 114;
            return (
              <div
                key={key}
                className="t-px-3 t-py-2.5 t-flex-shrink-0 t-text-text-60 t-text-body-sm t-font-semibold"
                style={{ minWidth: colWidth }}
              >
                <div className="t-flex t-justify-end t-ml-4">
                  <AmountSuperScript amount={totals[key]} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <>
      <motion.tr
        key="vendor-breakdown"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height, opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.05 }}
        className="t-w-full t-z-filter-head t-overscroll-x-none"
      >
        <td className="t-p-0" height={height} colSpan={parentColumnCount - 1}>
          <div ref={ref}>{content}</div>
        </td>
        <td
          height={height}
          className="t-p-0 t-sticky sm:!t-static t-right-0 t-align-top t-drop-shadow-table-right-pin t-bg-white t-z-[3]"
          style={{
            width: `${rightPinWidth}px`,
            minWidth: `${rightPinWidth}px`,
          }}
        >
          {!isFetching && (
            <div className="t-flex t-flex-col">
              <div
                className="!t-bg-surface-lighter-grey t-border-0 t-border-b t-border-solid t-border-neutral-0 t-flex-shrink-0"
                style={{ minHeight: "52px" }}
              />
              {filteredVendors.map((vendor, i) => {
                const vendorTotal = displayPeriodKeys.reduce(
                  (sum, key) => sum + (vendor.period[key]?.amount ?? 0),
                  0
                );
                return (
                  <div
                    key={i}
                    className="t-py-2.5 t-px-3 t-text-body-sm t-text-text-60 t-flex t-justify-end t-items-center t-border-0 t-border-b t-border-solid t-border-neutral-0"
                  >
                    <span
                      className={classNames({
                        "t-invisible": !showPinnedTotalColumn,
                      })}
                    >
                      <AmountSuperScript amount={vendorTotal} />
                    </span>
                  </div>
                );
              })}
              {vendors.length > 0 && (
                <div className="t-py-2.5 t-px-3 t-text-body-sm t-text-text-60 t-font-semibold t-flex t-justify-end t-items-center !t-bg-surface-lighter-grey">
                  <span
                    className={classNames({
                      "t-invisible": !showPinnedTotalColumn,
                    })}
                  >
                    <AmountSuperScript
                      amount={vendors.reduce(
                        (sum, v) =>
                          sum +
                          displayPeriodKeys.reduce(
                            (s, k) => s + (v.period[k]?.amount ?? 0),
                            0
                          ),
                        0
                      )}
                    />
                  </span>
                </div>
              )}
            </div>
          )}
        </td>
      </motion.tr>

      {selectFolderModal.isOpen && (
        <SelectFolderForUpload
          showModal={selectFolderModal.isOpen}
          closeModal={selectFolderModal.close}
          onUpload={(folderId) => exportSummary(false, folderId)}
        />
      )}
      {savedToInkleDocsModal.isOpen && savedDocumentId && (
        <SavedToInkleDocsModal
          isOpen={savedToInkleDocsModal.isOpen}
          close={savedToInkleDocsModal.close}
          fileId={savedDocumentId}
          reportType="Vendor Summary"
        />
      )}
    </>
  );
};
